import { useCallback, useEffect, useMemo } from 'react';
import { is } from '@client/helpers/dom.guards';
import { isInteractiveFormControlElement } from '@client/helpers/dom.helpers';
import { shouldIgnoreKeyboardShortcut } from '@shared/helpers/event.helpers';
import { expressionPanel } from '@features/expressions/ExpressionInputPanel/ExpressionInputPanel';
import { INLINE_EDITOR_ATTRIBUTE } from '@features/proposals/ElementListRow/ElementListRow';
import {
  resolveClickSelection,
  resolveSelectAllVisible,
  type GroupIdOf,
} from './list-multi-select.helpers';
import {
  clearListSelection,
  setListSelection,
  useListSelection,
} from './list-selection.store';

/**
 * A click that lands on an inline editor rather than on the row itself. The row
 * binds `onClick` on its whole box, so focusing the quantity field or opening
 * the product picker bubbles up as a row click.
 */
const isEditorClick = (event: React.MouseEvent): boolean => {
  const { target } = event;
  if (!is.Element(target)) return false;
  return (
    isInteractiveFormControlElement(target) ||
    target.closest(`[${INLINE_EDITOR_ATTRIBUTE}]`) !== null
  );
};

export type SelectRowHandler = (rowId: string, event: React.MouseEvent) => void;

export type ListMultiSelect = {
  /** Always contains `activeRowId`; see the union below. */
  selectedIds: ReadonlySet<string>;
  anchorId: string | undefined;
  selectRow: SelectRowHandler;
  clear: () => void;
};

/**
 * Multi-selection for a flat list of rows (DEV-3456), backed by
 * {@link useListSelection} — the row list and the header's conversion editor are
 * siblings in ElementsView, so the selection cannot live in either subtree.
 * Registers Cmd/Ctrl+A to select every currently visible row in the active
 * import (respects status filters; never spans two files).
 *
 * The active row lives in the URL. When the store is empty (deep link / first
 * paint), selection is derived as that row alone. Once the store has a
 * selection, it wins — so a plain click is not re-expanded by the previous
 * URL id while navigation catches up.
 */
export const useListMultiSelect = (
  orderedIds: readonly string[],
  activeRowId: string | undefined,
  groupIdOf: GroupIdOf,
): ListMultiSelect => {
  const state = useListSelection();

  const selectedIds = useMemo(() => {
    const { selectedIds: stored } = state;
    if (activeRowId === undefined) return stored;

    // A selection left over from another import yields to the active row: a
    // blind union would let the selection span two files.
    const [first] = stored;
    if (first !== undefined && groupIdOf(first) !== groupIdOf(activeRowId)) {
      return new Set([activeRowId]);
    }

    // URL is the only selection source until the user multi-selects.
    if (stored.size === 0) return new Set([activeRowId]);

    // Plain click / arrow already replaced `stored`; do not re-add the previous
    // URL id while navigation catches up (that looked like sticky multi-select).
    if (!stored.has(activeRowId)) return stored;

    return stored;
  }, [state, activeRowId, groupIdOf]);

  // Derived collapse is not enough: Backspace/skip read the store, not this
  // memo. Write the active row back so bulk actions cannot span two files.
  useEffect(() => {
    if (activeRowId === undefined) return;
    const { selectedIds: stored } = state;
    const [first] = stored;
    if (first === undefined) return;
    if (groupIdOf(first) === groupIdOf(activeRowId)) return;
    setListSelection({
      selectedIds: new Set([activeRowId]),
      anchorId: activeRowId,
    });
  }, [state, activeRowId, groupIdOf]);

  const selectRow = useCallback<SelectRowHandler>(
    (rowId, event) => {
      const shiftKey = event.shiftKey;
      const metaOrCtrl = event.metaKey || event.ctrlKey;

      if (isEditorClick(event)) {
        // Shift/Cmd on Select product keep Finder multi-select so the user can
        // build a set and then assign one product/recipe to all of them.
        if (!shiftKey && !metaOrCtrl) {
          // Plain editor click: keep multi-select when editing a selected row;
          // otherwise switch to the row being edited.
          if (selectedIds.has(rowId)) return;
          setListSelection({
            selectedIds: new Set([rowId]),
            anchorId: rowId,
          });
          return;
        }
      }

      setListSelection(
        resolveClickSelection({
          orderedIds,
          selectedIds,
          // Shift+click after plain arrow navigation has no stored anchor; the
          // active row is what the user would expect to extend from.
          anchorId: state.anchorId ?? activeRowId,
          targetId: rowId,
          shiftKey,
          metaOrCtrl,
          groupIdOf,
        }),
      );
    },
    [orderedIds, selectedIds, state.anchorId, activeRowId, groupIdOf],
  );

  const clear = useCallback(() => clearListSelection(), []);

  // Cmd/Ctrl+A → every visible row in the active import (filtered list).
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (shouldIgnoreKeyboardShortcut(event) || expressionPanel.isOpen()) {
        return;
      }
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) {
        return;
      }
      if (event.key.toLowerCase() !== 'a') return;

      const next = resolveSelectAllVisible(
        orderedIds,
        state.anchorId ?? activeRowId,
        groupIdOf,
      );
      if (!next) return;

      event.preventDefault();
      event.stopPropagation();
      setListSelection(next);
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [orderedIds, state.anchorId, activeRowId, groupIdOf]);

  return { selectedIds, anchorId: state.anchorId, selectRow, clear };
};
